import { useEffect, useState } from 'react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { portalApi, type PortalChannel } from '@/api/portal';

const payTypeLabels: Record<string, string> = {
  alipay: '支付宝',
  alipay_qr: '支付宝扫码',
  alipay_h5: '支付宝 H5',
  wechat: '微信支付',
  wechat_qr: '微信扫码',
  wechat_h5: '微信 H5',
  usdt_trc20: 'USDT-TRC20',
};

function formatPayTypes(value: string) {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => payTypeLabels[item.toLowerCase()] || item)
    .join('、');
}

function formatFeeRate(value: number) {
  return `${(Number(value || 0) / 100).toFixed(2)}%`;
}

export default function ChannelsPage() {
  const [channels, setChannels] = useState<PortalChannel[]>([]);
  const [channelCode, setChannelCode] = useState('');
  const [payType, setPayType] = useState('');
  const [currency, setCurrency] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadChannels = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await portalApi.channels({ channelCode, payType, currency, pageNum: 1, pageSize: 100 });
      setChannels(result.channelList || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载通道失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    portalApi.channels({ pageNum: 1, pageSize: 100 })
      .then((result) => {
        if (active) setChannels(result.channelList || []);
      })
      .catch((err) => {
        if (active) setError(err instanceof Error ? err.message : '加载通道失败');
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <DashboardLayout>
      <div className="space-y-5">
        <div className="tech-card p-4 md:p-5">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_180px_auto] gap-3">
            <input value={channelCode} onChange={(e) => setChannelCode(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void loadChannels()} placeholder="搜索通道编码" className="tech-input px-3 py-2.5 text-sm" />
            <input value={payType} onChange={(e) => setPayType(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void loadChannels()} placeholder="搜索支付方式，如 alipay" className="tech-input px-3 py-2.5 text-sm" />
            <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="tech-input px-3 py-2.5 text-sm">
              <option value="">全部币种</option>
              <option value="CNY">CNY</option>
              <option value="USDT">USDT</option>
            </select>
            <button onClick={() => void loadChannels()} disabled={loading} className="tech-btn-primary px-5 py-2.5 text-sm disabled:opacity-60">
              {loading ? '查询中...' : '查询'}
            </button>
          </div>
        </div>

        {error && <div className="rounded-lg border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>}

        <div className="tech-card overflow-hidden">
          <div className="flex items-center justify-between border-b border-slate-700/60 px-5 py-4">
            <div>
              <h3 className="font-semibold text-slate-100">可用支付通道</h3>
              <p className="mt-1 text-xs text-slate-500">仅展示当前已启用通道，费率已包含您的商户加点。</p>
            </div>
            <span className="rounded-full bg-blue-500/10 px-3 py-1 text-xs text-blue-300">共 {channels.length} 条</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left">
              <thead className="bg-slate-800/40 text-xs text-slate-500">
                <tr>
                  <th className="px-5 py-3 font-medium">通道编码</th>
                  <th className="px-5 py-3 font-medium">通道名称</th>
                  <th className="px-5 py-3 font-medium">支付方式</th>
                  <th className="px-5 py-3 font-medium">币种</th>
                  <th className="px-5 py-3 text-right font-medium">商户费率</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {channels.map((channel) => (
                  <tr key={channel.channelCode} className="transition-colors hover:bg-slate-800/30">
                    <td className="px-5 py-4 font-mono text-sm text-blue-300">{channel.channelCode}</td>
                    <td className="px-5 py-4 text-sm text-slate-200">{channel.name}</td>
                    <td className="px-5 py-4 text-sm text-slate-400">{formatPayTypes(channel.payTypes)}</td>
                    <td className="px-5 py-4"><span className="rounded-md border border-slate-700 bg-slate-800/70 px-2 py-1 text-xs text-slate-300">{channel.currency}</span></td>
                    <td className="px-5 py-4 text-right font-mono text-sm text-emerald-300">{formatFeeRate(channel.feeRate)}</td>
                  </tr>
                ))}
                {!loading && channels.length === 0 && (
                  <tr><td colSpan={5} className="px-5 py-14 text-center text-sm text-slate-500">没有符合条件的可用通道</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
